// ---- Agent Access Request API ----

export interface AgentAccessRequestDto {
  requestId: string;
  agentId: string;
  agentName: string | null;
  userId: string;
  userName: string | null;
  userEmail: string | null;
  status: string; // "Pending" | "Approved" | "Rejected"
  requestedAt: string;
  resolvedAt: string | null;
  resolvedBy: string | null;
}

export interface AccessRequestListResponse {
  requests: AgentAccessRequestDto[];
}

export interface MyAccessRequestDto {
  agentId: string;
  status: string; // "Pending" | "Approved" | "Rejected"
  requestedAt: string;
}

export interface MyAccessRequestListResponse {
  requests: MyAccessRequestDto[];
}

export const requestAgentAccess = async (agentId: string): Promise<AgentAccessRequestDto> => {
  const response = await fetch(`/api/agents/${encodeURIComponent(agentId)}/request-access`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to request access: ${response.status}`);
  }
  return response.json();
};

export const fetchMyAccessRequests = async (): Promise<MyAccessRequestDto[]> => {
  const response = await fetch("/api/my-access-requests", {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) throw new Error(`Failed to fetch access requests: ${response.status}`);
  const data: MyAccessRequestListResponse = await response.json();
  return data.requests;
};

export const fetchPendingRequests = async (statusFilter?: string): Promise<AgentAccessRequestDto[]> => {
  const url = statusFilter
    ? `/api/access-requests?status=${encodeURIComponent(statusFilter)}`
    : "/api/access-requests";
  const response = await fetch(url, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) throw new Error(`Failed to fetch access requests: ${response.status}`);
  const data: AccessRequestListResponse = await response.json();
  return data.requests;
};

export const approveAccessRequest = async (requestId: string): Promise<void> => {
  const response = await fetch(`/api/access-requests/${encodeURIComponent(requestId)}/approve`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to approve request: ${response.status}`);
  }
};

export const rejectAccessRequest = async (requestId: string): Promise<void> => {
  const response = await fetch(`/api/access-requests/${encodeURIComponent(requestId)}/reject`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || `Failed to reject request: ${response.status}`);
  }
};
